import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPackage,
  FiFilter,
  FiRefreshCw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const money = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : `₹${Number(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const statusStyles = {
  ACTIVE: "bg-green-100 text-green-700",
  DRAFT: "bg-yellow-100 text-yellow-700",
  INACTIVE: "bg-gray-100 text-gray-700",
  ARCHIVED: "bg-red-100 text-red-700",
};

const lotSummary = (variant) => {
  const lots = variant?.inventoryLots || [];
  const availableQuantity = lots.reduce(
    (total, lot) =>
      total +
      Math.max(
        0,
        Number(lot.remainingQuantity || 0) - Number(lot.reservedQuantity || 0),
      ),
    0,
  );

  return { activeLot: lots[0] || null, availableQuantity };
};

export default function ProductsPage() {
  const navigate = useNavigate();
  const { accessToken, isLoading: isAuthLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [brandId, setBrandId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [seasonId, setSeasonId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [hsnCodeId, setHsnCodeId] = useState("");
  const [gstSlabId, setGstSlabId] = useState("");
  const [status, setStatus] = useState("");
  const [onlineVisible, setOnlineVisible] = useState("");
  const [inStock, setInStock] = useState("");
  const [filterOptions, setFilterOptions] = useState({});
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(25);
  const [pagination, setPagination] = useState({ page: 1, take: 25, total: 0, totalPages: 1 });
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadProducts = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({ page: String(page), take: String(take) });
      const values = { search, brandId, categoryId, subCategoryId, seasonId, unitId, hsnCodeId, gstSlabId, status, onlineVisible, inStock };
      Object.entries(values).forEach(([key, value]) => {
        if (value !== "") params.set(key, value);
      });
      const response = await apiRequest(`/products?${params.toString()}`, {
        accessToken,
      });
      setProducts(response.items || []);
      setPagination(response.pagination || { page: 1, take, total: 0, totalPages: 1 });
    } catch (err) {
      setError(err.message || "Unable to load products.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, take, search, brandId, categoryId, subCategoryId, seasonId, unitId, hsnCodeId, gstSlabId, status, onlineVisible, inStock]);

  const loadFilterOptions = useCallback(async () => {
    if (!accessToken) return;
    try {
      const response = await apiRequest("/products/filters", { accessToken });
      setFilterOptions(response || {});
    } catch (err) {
      setError(err.message || "Unable to load product filters.");
    }
  }, [accessToken]);

  useEffect(() => {
    if (isAuthLoading || !accessToken) return;

    loadFilterOptions();
    loadProducts();
  }, [isAuthLoading, accessToken, loadProducts, loadFilterOptions]);

  const activeCount = products.filter((product) => product.status === "ACTIVE").length;
  const draftCount = products.filter((product) => product.status === "DRAFT").length;
  const activeFilterCount = [brandId, categoryId, subCategoryId, seasonId, unitId, hsnCodeId, gstSlabId, status, onlineVisible, inStock].filter(Boolean).length;

  const resetFilters = () => {
    setSearch(""); setBrandId(""); setCategoryId(""); setSubCategoryId(""); setSeasonId(""); setUnitId(""); setHsnCodeId(""); setGstSlabId(""); setStatus(""); setOnlineVisible(""); setInStock(""); setPage(1);
  };
  const updateFilter = (setter) => (value) => { setter(value); setPage(1); };

  const handleDelete = async (product) => {
    const confirmed = window.confirm(
      `Archive "${product.name}"? Its variants will also be archived.`,
    );

    if (!confirmed) return;

    try {
      await apiRequest(`/products/${product.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadProducts();
    } catch (err) {
      alert(err.message || "Unable to archive product.");
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-5 mb-8 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Products</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Products Management
          </h1>

          <p className="mt-2 text-gray-500">
            Manage product identity and variants. Prices and stock are managed
            through purchase lots.
          </p>
        </div>

        <button
          onClick={() => navigate("/admindashboard/masters/products/add")}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white hover:bg-primary-800"
        >
          <FiPlus />
          Add Product
        </button>
      </div>

      <div className="grid gap-5 mb-8 md:grid-cols-3">
        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Total Products</p>
          <h2 className="mt-2 text-3xl font-bold">{pagination.total}</h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Active on this page</p>
          <h2 className="mt-2 text-3xl font-bold text-green-600">
            {activeCount}
          </h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Draft on this page</p>
          <h2 className="mt-2 text-3xl font-bold text-yellow-600">
            {draftCount}
          </h2>
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 p-5">
          <div className="relative w-full md:w-80">
            <FiSearch
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search product, SKU, barcode, lot or supplier batch..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => setShowFilters((value) => !value)} className={`inline-flex h-12 items-center gap-2 rounded-xl border px-4 text-sm font-semibold ${showFilters || activeFilterCount ? "border-primary-300 bg-primary-50 text-primary-800" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}><FiFilter /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}</button>
            <button type="button" onClick={() => { resetFilters(); loadProducts(); }} className="inline-flex h-12 items-center gap-2 rounded-xl border border-gray-200 px-4 text-sm font-medium text-gray-600 hover:bg-gray-50"><FiRefreshCw /> Reset</button>
          </div>
        </div>

        {showFilters ? (
          <div className="grid gap-3 border-b border-gray-100 bg-gray-50/70 p-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <select
              value={brandId}
              onChange={(event) => updateFilter(setBrandId)(event.target.value)}
              className="h-12 rounded-xl border border-gray-200 px-4"
            >
              <option value="">All Brands</option>

              {(filterOptions.brands || []).map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>

            <select
              value={categoryId}
              onChange={(event) => updateFilter(setCategoryId)(event.target.value)}
              className="h-12 rounded-xl border border-gray-200 px-4"
            >
              <option value="">All Categories</option>

              {(filterOptions.categories || []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            <select
              value={status}
              onChange={(event) => updateFilter(setStatus)(event.target.value)}
              className="h-12 rounded-xl border border-gray-200 px-4"
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="DRAFT">Draft</option>
              <option value="INACTIVE">Inactive</option>
              <option value="ARCHIVED">Archived</option>
            </select>
            <select value={subCategoryId} onChange={(event) => updateFilter(setSubCategoryId)(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4"><option value="">All Sub-categories</option>{(filterOptions.subCategories || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
            <select value={seasonId} onChange={(event) => updateFilter(setSeasonId)(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4"><option value="">All Seasons</option>{(filterOptions.seasons || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
            <select value={unitId} onChange={(event) => updateFilter(setUnitId)(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4"><option value="">All Units</option>{(filterOptions.units || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select>
            <select value={hsnCodeId} onChange={(event) => updateFilter(setHsnCodeId)(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4"><option value="">All HSN codes</option>{(filterOptions.hsnCodes || []).map((item) => <option key={item.id} value={item.id}>{item.code}{item.description ? ` — ${item.description}` : ""}</option>)}</select>
            <select value={gstSlabId} onChange={(event) => updateFilter(setGstSlabId)(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4"><option value="">All GST rules</option>{(filterOptions.gstSlabs || []).map((item) => <option key={item.id} value={item.id}>{item.ruleGroup} · {Number(item.rate)}%</option>)}</select>
            <select value={onlineVisible} onChange={(event) => updateFilter(setOnlineVisible)(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4"><option value="">Online: all</option><option value="true">Visible online</option><option value="false">Not visible online</option></select>
            <select value={inStock} onChange={(event) => updateFilter(setInStock)(event.target.value)} className="h-12 rounded-xl border border-gray-200 px-4"><option value="">Stock: all</option><option value="true">In stock</option></select>
          </div>
        ) : null}

        {error && (
          <div className="m-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                {[
                  "S.No.",
                  "Product",
                  "Brand",
                  "Category",
                  "Lot Pricing",
                  "Available Stock",
                  "Variants",
                  "Status",
                  "Actions",
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-6 py-4 text-left text-xs font-medium uppercase text-gray-500"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Loading products…
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No products found.
                  </td>
                </tr>
              ) : (
                products.map((product, index) => {
                  const variants = product.variants || [];
                  const firstVariant = variants[0];
                  const { activeLot, availableQuantity } = lotSummary(firstVariant);

                  return (
                    <tr
                      key={product.id}
                      className="border-b border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 text-gray-500">{(pagination.page - 1) * pagination.take + index + 1}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() =>
                            navigate(
                              `/admindashboard/masters/products/${product.id}`,
                            )
                          }
                          className="flex items-center gap-3 text-left"
                        >
                          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                            <FiPackage size={20} />
                          </div>

                          <div>
                            <p className="font-semibold text-gray-900">
                              {product.name}
                            </p>

                            <p className="text-xs text-gray-500">
                              Code: {product.productCode || "—"} · SKU:{" "}
                              {firstVariant?.sku || "—"}
                            </p>
                          </div>
                        </button>
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-700">
                        {product.brand?.name || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-700">
                        {product.category?.name || "—"}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {activeLot ? (
                          <div>
                            <p className="font-medium text-gray-900">
                              MRP {money(activeLot.mrp)}
                            </p>
                            <p className="mt-1 text-xs text-gray-500">
                              POS {money(activeLot.posPrice)} · Online{" "}
                              {money(activeLot.onlinePrice)}
                            </p>
                          </div>
                        ) : (
                          <span className="text-gray-500">No active lot</span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm font-medium">
                        {Number(availableQuantity).toLocaleString("en-IN", {
                          maximumFractionDigits: 3,
                        })}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-700">
                        {variants.length}
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-medium ${
                            statusStyles[product.status] ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {product.status}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            title="View product"
                            onClick={() =>
                              navigate(
                                `/admindashboard/masters/products/${product.id}`,
                              )
                            }
                            className="rounded-lg p-2 text-primary-700 hover:bg-primary-50"
                          >
                            <FiEye />
                          </button>

                          <button
                            title="Edit product"
                            onClick={() =>
                              navigate(
                                `/admindashboard/masters/products/${product.id}/edit`,
                              )
                            }
                            className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"
                          >
                            <FiEdit2 />
                          </button>

                          <button
                            title="Archive product"
                            onClick={() => handleDelete(product)}
                            className="rounded-lg p-2 text-red-600 hover:bg-red-50"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col gap-4 border-t border-gray-100 bg-gray-50/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600">Showing <strong>{products.length ? (pagination.page - 1) * pagination.take + 1 : 0}–{(pagination.page - 1) * pagination.take + products.length}</strong> of <strong>{pagination.total}</strong> products</p>
          <div className="flex items-center gap-3"><select value={take} onChange={(event) => { setTake(Number(event.target.value)); setPage(1); }} className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm"><option value="25">25 / page</option><option value="50">50 / page</option><option value="100">100 / page</option></select><button type="button" disabled={pagination.page <= 1} onClick={() => setPage((value) => value - 1)} className="inline-flex h-10 items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40"><FiChevronLeft /> Previous</button><span className="text-sm font-medium text-gray-700">Page {pagination.page} / {pagination.totalPages}</span><button type="button" disabled={pagination.page >= pagination.totalPages} onClick={() => setPage((value) => value + 1)} className="inline-flex h-10 items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40">Next <FiChevronRight /></button></div>
        </div>
      </div>
    </div>
  );
}
